/**
 * Board pack section renderers - institutional ledger aesthetic.
 * Flat, border-separated, serif headings, mono numbers, minimalist
 * recharts. No cards, no shadows inside the document.
 */
import {
  Bar, BarChart, CartesianGrid, ComposedChart, Line, LineChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

export const gbp = (v) =>
  v == null ? "–" : `${v < 0 ? "-" : ""}£${Math.abs(Math.round(v)).toLocaleString("en-GB")}`;
export const gbpShort = (v) => {
  if (v == null) return "–";
  const a = Math.abs(v), s = v < 0 ? "-" : "";
  if (a >= 1e6) return `${s}£${(a / 1e6).toFixed(2)}m`;
  if (a >= 1e3) return `${s}£${Math.round(a / 1e3)}k`;
  return `${s}£${Math.round(a)}`;
};
export const pctf = (v) => (v == null ? "–" : `${v}%`);
export const deltaPct = (cur, ly) => {
  if (cur == null || ly == null || !ly) return null;
  return Math.round((100 * (cur - ly)) / Math.abs(ly));
};

const NAVY = "#1B4F8A";
const TEAL = "#1F7468";
const INK = "#0A192F";

/* ---------- primitives ---------- */

export function Hero({ items }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6 mt-6">
      {items.filter(Boolean).map((it) => (
        <div key={it.label} className="min-w-0">
          <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#6B7280]">{it.label}</p>
          <p className={`font-mono tabular-nums tracking-tight text-[26px] leading-tight mt-1 ${it.tone || "text-[#0A192F]"}`}>
            {it.value}
          </p>
          {it.sub != null && <p className="font-mono text-[11px] text-[#6B7280] mt-0.5">{it.sub}</p>}
        </div>
      ))}
    </div>
  );
}

export function Delta({ cur, ly, invert = false }) {
  const d = deltaPct(cur, ly);
  if (d == null) return null;
  const good = invert ? d < 0 : d >= 0;
  return (
    <span className={good ? "text-[#166534]" : "text-[#991B1B]"}>
      {d >= 0 ? "+" : ""}{d}% vs LY
    </span>
  );
}

export function Ledger({ cols, rows, testid, footnote }) {
  return (
    <div className="mt-6 overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-y-2 border-slate-800">
            {cols.map((c, i) => (
              <th key={i} className={`font-mono uppercase tracking-[0.12em] text-[10px] text-[#4B5563] font-medium py-2 ${i === 0 ? "text-left pr-4" : "text-right pl-4"}`}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className={`border-b border-slate-200 ${r.emphasis ? "font-semibold" : ""}`}>
              {r.cells.map((cell, ci) => (
                <td key={ci} className={`py-2 ${ci === 0 ? "text-left pr-4 text-[#0A192F]" : "text-right pl-4 font-mono tabular-nums"} ${r.tone && ci > 0 ? r.tone : ci > 0 ? "text-[#0A192F]" : ""}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {footnote && <p className="font-mono text-[10px] text-[#6B7280] mt-2">{footnote}</p>}
    </div>
  );
}

const tooltipStyle = {
  fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
  border: "1px solid #E5E7EB", borderRadius: 0, background: "#fff",
};
const axisTick = { fontSize: 10, fontFamily: "'JetBrains Mono', monospace", fill: "#6B7280" };

export function ChartBlock({ title, children, height = 210 }) {
  return (
    <div className="mt-8">
      <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#6B7280] mb-2">{title}</p>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
      </div>
    </div>
  );
}

export function Unavailable({ d }) {
  return (
    <p className="font-mono text-xs text-[#6B7280] mt-4">
      Data source unavailable at compile time{d?.error ? ` (${d.error})` : ""}. Recompile when the source is reachable.
    </p>
  );
}

export function StaleNote({ d }) {
  if (!d?.stale) return null;
  return (
    <p className="font-mono text-[10px] text-[#B45309] mt-3">
      Figures from the last QuickBooks snapshot{d.as_of ? ` (${String(d.as_of).slice(0, 10)})` : ""}.
    </p>
  );
}

/* ---------- sections ---------- */

export function FinancialSection({ d }) {
  if (!d.available) return <Unavailable d={d} />;
  const p = d.period, ly = d.ly || {};
  if (!p) return <p className="font-mono text-xs text-[#6B7280] mt-4">No ledger months inside this period yet.</p>;
  const cascade = [
    { label: "Revenue", k: "revenue" },
    { label: "Cost of sales", k: "cos", invert: true },
    { label: "Gross profit", k: "gp", emphasis: true, m: "gp_margin_pct" },
    { label: "People", k: "people", invert: true },
    { label: "Contribution", k: "contribution", emphasis: true, m: "contribution_margin_pct" },
    { label: "Overheads", k: "overheads", invert: true },
    { label: "EBITDA", k: "ebitda", emphasis: true, m: "ebitda_margin_pct" },
  ];
  const track = d.track_record || [];
  const ltmData = (d.ltm_series || []).map((r) => ({ ...r, m: (r.month || "").slice(2) }));
  return (
    <>
      <Hero items={[
        { label: "Revenue", value: gbpShort(p.revenue), sub: <Delta cur={p.revenue} ly={ly.revenue} /> },
        { label: "Gross profit", value: gbpShort(p.gp), sub: `${pctf(p.gp_margin_pct)} margin` },
        { label: "EBITDA", value: gbpShort(p.ebitda), sub: <span>{pctf(p.ebitda_margin_pct)} · <Delta cur={p.ebitda} ly={ly.ebitda} /></span>, tone: p.ebitda < 0 ? "text-[#991B1B]" : "" },
        d.ltm ? { label: "LTM EBITDA", value: gbpShort(d.ltm.ltm_ebitda), sub: `${pctf(d.ltm.ltm_ebitda_margin_pct)} · 12m to ${d.ltm.month}` } : null,
      ]} />
      <Ledger
        testid="board-fin-cascade"
        cols={["", "Period", "Same period LY", "Change"]}
        rows={cascade.map((c) => {
          const dp = deltaPct(p[c.k], ly[c.k]);
          return {
            emphasis: c.emphasis,
            cells: [
              c.m ? `${c.label}  ·  ${pctf(p[c.m])}` : c.label,
              gbp(p[c.k]),
              gbp(ly[c.k]),
              dp == null ? "–" : (
                <span className={(c.invert ? dp <= 0 : dp >= 0) ? "text-[#166534]" : "text-[#991B1B]"}>
                  {dp >= 0 ? "+" : ""}{dp}%
                </span>
              ),
            ],
          };
        })}
        footnote={p.partial
          ? `Year in progress: covers the ${p.months_covered} complete months to ${monthName(p.through)}. The LY column mirrors the same months of the prior year, like for like.`
          : undefined}
      />
      {track.length > 1 && (
        <Ledger
          testid="board-fin-track"
          cols={["Track record", ...track.map((t) => `${t.year}${t.partial ? " YTD" : ""}`)]}
          rows={[
            { cells: ["Revenue", ...track.map((t) => gbp(t.revenue))], emphasis: true },
            { cells: ["Gross margin", ...track.map((t) => pctf(t.gp_margin_pct))] },
            { cells: ["EBITDA", ...track.map((t) => gbp(t.ebitda))], emphasis: true },
            { cells: ["EBITDA margin", ...track.map((t) => pctf(t.ebitda_margin_pct))] },
            { cells: ["People cost / revenue", ...track.map((t) => pctf(t.people_pct))] },
          ]}
        />
      )}
      {ltmData.length > 3 && (
        <ChartBlock title="Rolling 12-month revenue and EBITDA">
          <ComposedChart data={ltmData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="m" tick={axisTick} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} interval="preserveStartEnd" />
            <YAxis tick={axisTick} tickLine={false} axisLine={false} tickFormatter={(v) => gbpShort(v)} width={54} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [gbp(v), n === "revenue" ? "LTM revenue" : "LTM EBITDA"]} />
            <Line type="monotone" dataKey="revenue" stroke={NAVY} strokeWidth={1.75} dot={false} />
            <Line type="monotone" dataKey="ebitda" stroke={TEAL} strokeWidth={1.75} dot={false} />
          </ComposedChart>
        </ChartBlock>
      )}
      {d.twelve_month && (
        <Ledger
          testid="board-fin-split"
          cols={["Trailing 12 months", "£"]}
          rows={[
            { cells: ["Operating profit (trading business)", gbp(d.twelve_month.operating)] },
            { cells: ["Investment result (below the line)", gbp(d.twelve_month.investment)] },
            { cells: ["Net profit", gbp(d.twelve_month.net)], emphasis: true },
          ]}
          footnote="Operating profit excludes portfolio revaluations, ventures, dividends and interest."
        />
      )}
      <StaleNote d={d} />
    </>
  );
}

export function CashSection({ d }) {
  if (!d.available) return <Unavailable d={d} />;
  return (
    <>
      <Hero items={[
        { label: "Cash at bank", value: gbpShort(d.cash_total) },
        { label: "War chest", value: gbpShort(d.war_chest), sub: "portfolios + ventures" },
        { label: "Cash + investments", value: gbpShort(d.cash_and_investments) },
        { label: "Net position", value: gbpShort(d.net_position), sub: "after creditors + VAT" },
      ]} />
      <Ledger
        testid="board-cash-ledger"
        cols={["Working capital", ""]}
        rows={[
          { cells: ["Cash runway at current burn", d.runway_months != null ? `${d.runway_months} months` : "–"] },
          { cells: ["Debtor days (DSO)", d.dso_days != null ? `${d.dso_days} days` : "–"] },
          { cells: ["Creditor days (DPO)", d.dpo_days != null ? `${d.dpo_days} days` : "–"] },
          { cells: ["VAT set-aside", `${gbp(d.vat_set_aside)}${d.vat_due_date ? `  ·  due ${d.vat_due_date}` : ""}`] },
          { cells: ["13-week forecast low point", `${gbp(d.low_point?.balance ?? d.low_point?.low ?? null)}${d.low_point?.week || d.low_point?.date ? `  ·  ${d.low_point.week || d.low_point.date}` : ""}`] },
        ]}
      />
      <StaleNote d={d} />
    </>
  );
}

export function SalesSection({ d }) {
  if (!d.available) return <Unavailable d={d} />;
  const p = d.period || {}, ly = d.ly || {}, qb = d.quoted_book;
  const trend = d.quarterly_trend || [];
  return (
    <>
      <Hero items={[
        { label: "Commissioned", value: gbpShort(p.commissioned_value), sub: <span>{p.commissioned_n ?? "–"} wins · <Delta cur={p.commissioned_value} ly={ly.commissioned_value} /></span>, tone: "text-[#1F7468]" },
        { label: "Quoted in period", value: gbpShort(p.quoted_value), sub: `${p.quoted_n ?? "–"} quote lines` },
        qb ? { label: "Live quoted book", value: gbpShort(qb.book_value), sub: `${qb.count ?? "–"} opportunities · ${qb.hot ?? 0} hot` } : null,
        qb ? { label: "Expected value", value: gbpShort(qb.expected_value), sub: "probability weighted" } : null,
      ]} />
      {trend.length > 3 && (
        <ChartBlock title="Commissioned per quarter, trailing 8 quarters" height={190}>
          <BarChart data={trend} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="quarter" tick={axisTick} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} />
            <YAxis tick={axisTick} tickLine={false} axisLine={false} tickFormatter={(v) => gbpShort(v)} width={54} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => [gbp(v), "Commissioned"]} />
            <Bar dataKey="value" fill={NAVY} maxBarSize={34} />
          </BarChart>
        </ChartBlock>
      )}
      {(p.top_wins || []).length > 0 && (
        <Ledger
          testid="board-sales-wins"
          cols={["Largest wins in the period", "Value"]}
          rows={p.top_wins.map((w) => ({ cells: [w.client, gbp(w.value)] }))}
          footnote={`Research teams only, MediLAB excluded. Wins counted by commission date, one per proposal chain.${d.through ? ` Period runs to ${monthName(d.through)} to date; LY mirrors the same window.` : ""}`}
        />
      )}
    </>
  );
}

export function ClientsSection({ d }) {
  if (!d.available) return <Unavailable d={d} />;
  const c = d.concentration || {}, nr = d.new_vs_repeat || {};
  const trend = (d.concentration_trend || []).map((t) => ({ ...t }));
  return (
    <>
      <Hero items={[
        { label: `Top-5 share · ${c.year || ""}`, value: pctf(c.top5_share), tone: c.top5_share > 50 ? "text-[#991B1B]" : "" },
        { label: "Top-10 share", value: pctf(c.top10_share) },
        { label: "Active clients", value: c.clients ?? "–" },
        { label: "Repeat revenue", value: pctf(nr.repeat_share), sub: nr.new_clients != null ? `${nr.new_clients} new clients` : "" },
      ]} />
      {trend.length > 1 && (
        <ChartBlock title="Client concentration by year" height={180}>
          <LineChart data={trend} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="year" tick={axisTick} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} />
            <YAxis tick={axisTick} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} width={38} domain={[0, 100]} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [`${v}%`, n === "top5_share" ? "Top 5" : "Top 10"]} />
            <Line type="monotone" dataKey="top5_share" stroke={NAVY} strokeWidth={1.75} dot={{ r: 2 }} />
            <Line type="monotone" dataKey="top10_share" stroke={TEAL} strokeWidth={1.75} dot={{ r: 2 }} />
          </LineChart>
        </ChartBlock>
      )}
      {(d.movers || []).length > 0 && (
        <Ledger
          testid="board-clients-league"
          cols={["Client", "All-time billed", "Committed this year", ""]}
          rows={d.movers.map((m) => ({
            cells: [
              m.label,
              gbp(m.total),
              m.committed_cur_year != null ? gbp(m.committed_cur_year) : "–",
              m.quiet ? <span className="text-[#B45309] font-mono text-[10px] uppercase tracking-wider">quiet 6m+</span> : "",
            ],
          }))}
        />
      )}
    </>
  );
}

export function DivisionsSection({ d }) {
  if (!d.available) return <Unavailable d={d} />;
  const rows = (d.rows || []).filter((r) => r.revenue || r.ly);
  if (!rows.length) return <p className="font-mono text-xs text-[#6B7280] mt-4">No divisional postings inside this period.</p>;
  const max = Math.max(...rows.map((r) => r.revenue || 0), 1);
  return (
    <>
      <Ledger
        testid="board-divisions-ledger"
        cols={["Division", "Revenue", "Share", "Same period LY", "Change"]}
        rows={rows.map((r) => {
          const dp = deltaPct(r.revenue, r.ly);
          return {
            cells: [
              <span key="d" className="inline-flex items-center gap-3">
                <span className="inline-block h-2 bg-[#1B4F8A]" style={{ width: `${Math.max(3, (56 * (r.revenue || 0)) / max)}px` }} />
                {r.division}
              </span>,
              gbp(r.revenue),
              pctf(r.share_pct),
              gbp(r.ly),
              dp == null ? "–" : <span className={dp >= 0 ? "text-[#166534]" : "text-[#991B1B]"}>{dp >= 0 ? "+" : ""}{dp}%</span>,
            ],
          };
        })}
        footnote={`Revenue by QuickBooks class.${d.through ? ` Covers the complete months to ${monthName(d.through)}; LY mirrors the same months.` : ""}`}
      />
      <StaleNote d={d} />
    </>
  );
}

const AGED_LABELS = [
  ["current", "Current"], ["b1_30", "1-30 days"], ["b31_60", "31-60 days"],
  ["b61_90", "61-90 days"], ["b91_plus", "90+ days"],
];

export function RisksSection({ d }) {
  if (!d.available) return <Unavailable d={d} />;
  const deb = d.debtors || {}, integ = d.integrity || {};
  const aged = deb.aged || null;
  const agedTotal = aged ? Object.values(aged).reduce((a, b) => a + b, 0) : 0;
  return (
    <>
      <Hero items={[
        { label: "Live loss-makers", value: d.loss_live_n ?? "–", tone: d.loss_live_n ? "text-[#991B1B]" : "text-[#166534]" },
        { label: "Overdue debtors", value: gbpShort(deb.overdue_ar), sub: deb.overdue_pct != null ? `${deb.overdue_pct}% of the book` : "", tone: deb.overdue_pct > 30 ? "text-[#991B1B]" : "" },
        { label: "Ledger mismatches", value: integ.mismatches ?? "–" },
        { label: "Won, no schedule", value: integ.missing_wip ?? "–" },
      ]} />
      {aged && agedTotal > 0 && (
        <div className="mt-8">
          <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#6B7280] mb-2">Debtor book ageing · {gbp(agedTotal)}</p>
          <div className="flex h-5 w-full border border-slate-300" data-testid="board-risks-aging">
            {AGED_LABELS.map(([k], i) => {
              const v = aged[k] || 0;
              if (!v) return null;
              const shades = ["#1B4F8A", "#5B7FA8", "#B45309", "#C2612B", "#991B1B"];
              return <div key={k} style={{ width: `${(100 * v) / agedTotal}%`, background: shades[i] }} title={`${AGED_LABELS[i][1]}: ${gbp(v)}`} />;
            })}
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2">
            {AGED_LABELS.map(([k, label], i) => (
              <span key={k} className="font-mono text-[10px] text-[#4B5563] inline-flex items-center gap-1.5">
                <span className="w-2 h-2 inline-block" style={{ background: ["#1B4F8A", "#5B7FA8", "#B45309", "#C2612B", "#991B1B"][i] }} />
                {label} {gbpShort(aged[k])}
              </span>
            ))}
          </div>
        </div>
      )}
      {(d.loss_live || []).length > 0 && (
        <Ledger
          testid="board-risks-losses"
          cols={["Live projects below 25% margin", "Revenue", "Margin"]}
          rows={d.loss_live.map((l) => ({
            cells: [`${l.label}${l.project ? ` · ${l.project}` : ""}`, gbp(l.revenue), pctf(l.margin_pct)],
            tone: "text-[#991B1B]",
          }))}
        />
      )}
      <StaleNote d={deb} />
    </>
  );
}

const monthName = (ym) => {
  if (!ym) return "–";
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
};

export function OutlookSection({ d }) {
  if (!d.available) return <Unavailable d={d} />;
  return (
    <>
      <Hero items={[
        { label: `Billed ${d.year || ""} YTD`, value: gbpShort(d.billed_ytd) },
        { label: "Committed full year", value: gbpShort(d.year_committed), sub: `+${gbpShort(d.scheduled_rest_of_year)} scheduled` },
        { label: "Projected from quotes", value: gbpShort(d.projected_in_year), sub: "in-year, probability weighted" },
        { label: "Year with projected", value: gbpShort(d.year_with_projected), tone: "text-[#1F7468]" },
      ]} />
      {(d.ladder || []).length > 0 && (
        <Ledger
          testid="board-outlook-ladder"
          cols={["Scheduled billings, next six months", ...d.ladder.map((l) => monthName(l.month))]}
          rows={[{ cells: ["Contracted instalments", ...d.ladder.map((l) => gbpShort(l.scheduled))] }]}
          footnote={d.scheduled_next_12m != null ? `Total contracted for the next 12 months: ${gbp(d.scheduled_next_12m)}.` : undefined}
        />
      )}
      {(d.light_months || []).length > 0 && (
        <p className="font-mono text-[11px] text-[#B45309] mt-4">
          Light months ahead: {d.light_months.map((m) => monthName(m.month)).join(", ")} (scheduled below 60% of run-rate).
        </p>
      )}
    </>
  );
}

export const SECTION_RENDERERS = {
  financial: FinancialSection,
  cash: CashSection,
  sales: SalesSection,
  clients: ClientsSection,
  divisions: DivisionsSection,
  risks: RisksSection,
  outlook: OutlookSection,
};
